import { Button, Stack, Text, Title } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { useSuspenseQueries } from "@tanstack/react-query";
import { useState } from "react";
import { StrictUndef } from "shared/api/httpClient";
import { GroupSelector } from "shared/components/organization/groups/groups";
import { handleRoleAssign, UserSchema } from "shared/components/organization/users/data";
import { groupsQueryOptions } from "shared/graphql/groups";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { permissionCheck, usePermissions } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { graphql } from "src/gql";
import { FullUserFragment, GroupLikeFragment, Permission } from "src/gql/graphql";

export const GroupAssignComponent = ({
    userIds,
    selectedRoleId,
    groupDiff,
    onClose,
}: {
    userIds: string[];
    selectedRoleId: string;
    groupDiff: Map<string, "add" | "remove">;
    onClose: () => void;
}) => {
    const { permissions } = usePermissions();
    const [selectedGroups, setSelectedGroups] = useState<GroupLikeFragment[]>([]);

    const assignGroupRole = useGraphqlMutation(muts.assignGroup);
    function isDisabledPredicate(g: GroupLikeFragment) {
        return !permissionCheck(permissions, Permission.GroupRolesManage, g.id);
    }

    useHotkeys([["Escape", () => onClose()]]);

    return (
        <Stack gap="xs">
            <Title order={3}>Select Group</Title>
            <Text>Select at least one group for this role.</Text>
            <GroupSelector
                selectedGroups={selectedGroups}
                onChange={(g, checked) => {
                    if (checked) {
                        setSelectedGroups(selectedGroups.concat(g));
                        groupDiff.set(g.id, "add");
                    } else {
                        setSelectedGroups(selectedGroups.filter((it) => it.id != g.id));
                        groupDiff.delete(g.id);
                    }
                }}
                shouldDisable={isDisabledPredicate}
                containerStyle={{ maxHeight: "55dvh" }}
            />
            <Button
                w="100%"
                disabled={selectedGroups.length == 0}
                onClick={() => {
                    handleRoleAssign(
                        userIds.flatMap((id) =>
                            selectedGroups.map((g) =>
                                assignGroupRole
                                    .mutateAsync({ userId: id, roleId: selectedRoleId, groupId: g.id })
                                    .then((res) => res.assignGroupRole.user as StrictUndef<FullUserFragment>),
                            ),
                        ),
                    ).catch(logger.error);
                }}
            >
                Save
            </Button>
        </Stack>
    );
};

export const GroupEditComponent = ({
    user,
    selectedRoleId,
    initialGroupIds,
    groupDiff,
    onClose,
}: {
    user: UserSchema;
    selectedRoleId: string;
    initialGroupIds: string[];
    groupDiff: Map<string, "add" | "remove">;
    onClose: () => void;
}) => {
    const { permissions } = usePermissions();

    const data = useSuspenseQueries({ queries: initialGroupIds.map(groupsQueryOptions.group) });
    const initialGroups = data.map((it) => it.data.group);
    const [selectedGroups, setSelectedGroups] = useState<GroupLikeFragment[]>(initialGroups);

    const assignGroupRole = useGraphqlMutation(muts.assignGroup);
    const unassignRole = useGraphqlMutation(
        graphql(`
            mutation GroupRoleManageUnassignRole($userId: UUID!, $userRoleId: UUID!) {
                unassignRole(userId: $userId, userRoleId: $userRoleId) {
                    ...FullUser
                }
            }
        `),
    );

    function isDisabledPredicate(g: GroupLikeFragment) {
        return !permissionCheck(permissions, Permission.GroupRolesManage, g.id);
    }

    return (
        <Stack gap="xs">
            <Title order={3}>Select Group</Title>
            <Text>Select at least one group for this role.</Text>
            <GroupSelector
                selectedGroups={selectedGroups}
                onChange={(g, checked) => {
                    if (checked) {
                        setSelectedGroups(selectedGroups.concat(g));
                        if (!initialGroups.some((it) => it.id == g.id)) groupDiff.set(g.id, "add");
                        else groupDiff.delete(g.id);
                    } else {
                        setSelectedGroups(selectedGroups.filter((it) => it.id != g.id));
                        if (initialGroups.some((it) => it.id == g.id)) groupDiff.set(g.id, "remove");
                        else groupDiff.delete(g.id);
                    }
                }}
                shouldDisable={isDisabledPredicate}
                containerStyle={{ maxHeight: "55dvh" }}
            />
            <Button
                w="100%"
                disabled={groupDiff.size == 0}
                onClick={() => {
                    const promises = Array.from(groupDiff).map(([id, op]) => {
                        if (op == "add")
                            return assignGroupRole
                                .mutateAsync({ userId: user.id, roleId: selectedRoleId, groupId: id })
                                .then((res) => res.assignGroupRole.user as StrictUndef<FullUserFragment>);
                        else {
                            const userRoleId = user.roles.find((r) => r.role.id == selectedRoleId && r.group?.id == id)?.id;
                            if (!userRoleId)
                                return Promise.reject(`userRoleId not found on user ${user.id} for role ${selectedRoleId} for group ${id}`);
                            else
                                return unassignRole
                                    .mutateAsync({ userId: user.id, userRoleId: userRoleId! })
                                    .then((res) => res.unassignRole as StrictUndef<FullUserFragment>);
                        }
                    });
                    handleRoleAssign(promises).then(groupDiff.clear).then(onClose).catch(logger.error);
                }}
            >
                Save
            </Button>
        </Stack>
    );
};

const muts = {
    assignGroup: graphql(`
        mutation GroupRoleManageAssignGroupRole($userId: UUID!, $roleId: UUID!, $groupId: UUID!) {
            assignGroupRole(params: { roleId: $roleId, userId: $userId, groupId: $groupId }) {
                user {
                    ...FullUser
                }
            }
        }
    `),
};
